import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';

import { BuyersListService } from '@core/services/buyers-list.service';
import { IBuyList } from '@shared/models/buyers-list';
import { hasShortMessage } from '@core/utils/premium-errors';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectAuthUser } from '@state/auth/auth.selectors';

@UntilDestroy()
@Component({
  selector: 'app-property-share-buyers-list',
  templateUrl: './share-buyers-list.component.html',
  styleUrls: [
    './share-buyers-list.component.scss',
    '../share-emails/share-emails.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyShareBuyersListComponent implements OnInit {
  @Input({ required: true }) propertyId: string = '';
  @Input() type: 'EMAIL' | 'SMS' = 'EMAIL';
  @Input() showCloseButton: boolean = false;
  @Output() handleClose = new EventEmitter<void>();

  protected buyersList: (IBuyList & {
    allowShareParsed: any;
    remainingTimeFormatted: string | null;
  })[] = [];
  protected loadingBuyersList = true;
  protected error = false;

  protected success = false;
  protected sending = false;
  protected selected = new Set<string>();

  protected multipleTypes = true;
  protected userHasOrganization = false;

  constructor(
    private buyersListService: BuyersListService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.store
      .select(selectAuthUser)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.userHasOrganization = !!user?.organization;

        if (this.userHasOrganization) {
          this.buyersListService
            .allBuyListFromOrganization({})
            .pipe(untilDestroyed(this))
            .subscribe({
              next: (res) => {
                this.loadingBuyersList = false;
                this.error = false;
                this.buyersList = res.allBuyListFromOrganization.results.map(
                  (item: any) => {
                    try {
                      const allowShareParsed = item.allowShare
                        ? JSON.parse(item.allowShare)
                        : null;

                      let remainingTimeFormatted: string | null = null;

                      if (
                        allowShareParsed &&
                        allowShareParsed['allow_share?'] === false
                      ) {
                        const totalSeconds = allowShareParsed.remaining_time;
                        const hours = Math.floor(totalSeconds / 3600);
                        const minutes = Math.floor((totalSeconds % 3600) / 60);

                        remainingTimeFormatted = `${hours}h ${minutes}m`;
                      }

                      return {
                        ...item,
                        allowShareParsed,
                        remainingTimeFormatted,
                      };
                    } catch (e) {
                      return {
                        ...item,
                        allowShareParsed: null,
                        remainingTimeFormatted: null,
                      };
                    }
                  }
                );
                this.cdr.detectChanges();
              },
              error: (err) => {
                this.loadingBuyersList = false;
                this.error = true;
                this.toastr.error(
                  'Unable to load buyers list. Please try again later.',
                  'Error'
                );
                this.cdr.detectChanges();
              },
            });
        } else {
          this.loadingBuyersList = false;
        }
      });
  }

  protected all_buy_lists_one_day_limit = false;
  shareWithList() {
    this.sending = true;
    this.success = false;
    this.buyersListService
      .sharePropertyWithBuyLists({
        buyLists: Array.from(this.selected),
        propertyId: this.propertyId,
        types: this.multipleTypes ? ['EMAIL', 'SMS'] : [this.type],
      })
      .subscribe({
        next: (res) => {
          const reached_limit = hasShortMessage(
            res,
            'all_buy_lists_one_day_limit'
          );
          if (reached_limit) {
            this.sending = false;
            this.all_buy_lists_one_day_limit = true;
            this.cdr.detectChanges();
            return;
          }

          this.success = true;
          this.sending = false;
          this.toastr.success(
            'Property shared with buyers list successfully.',
            'Success'
          );
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.sending = false;
          this.toastr.error(
            'Unable to share property with buyers list. Please try again later.',
            'Error'
          );
          this.cdr.detectChanges();
        },
      });
  }

  toggleSelection(id: string, checked: boolean): void {
    if (checked) {
      this.selected.add(id);
    } else {
      this.selected.delete(id);
    }
  }

  trackByFn(index: number, item: IBuyList): string {
    return item.id;
  }
}
